// src/public/modules/archives/animations.js
// Utilidades de animación reutilizables (flip, parpadeo) con configuración centralizada.
//
// Notas de integración:
// - D1 y D2: el parpadeo (startBlink) se ejecuta solo si `tarot_theme.tkta_flash_animation === 1`.
//   Si está desactivado (o reduce motion), el flujo continúa tras el flip y el loader
//   se coordina únicamente con el fade del tablero (`tableroFadeMs`).
// - D3: el parpadeo de imagen final sigue la misma regla (`tkta_flash_animation`),
//   y usa `blinkOneMs`/`blinkCount` de esta configuración.
// - Para cambiar tiempos globales, modifica aquí: `flipMs`, `blinkOneMs`, `blinkCount`,
//   `tableroFadeMs` y `flipEasing`. Los cambios impactan en todos los selectores.
// - Tiempos del loader centralizados:
//   * loaderDwellMs: retardo antes de iniciar el loader tras la última carta.
//   * loaderDurationMs: duración por defecto del loader (puede ser sobrescrita por admin).
// - Fase de conteo del tablero:
//   * tableroCountPhase: 'start' (por defecto) cuenta al iniciar el fade-in de la carta en tablero.
//     Usar 'end' si deseas esperar a que termine el fade antes de lanzar el loader.
// - Transiciones comunes:
//   * fadeEasing: easing a usar para opacidad en tablero y fade-out del selector (D2).
//   * transitionSlackMs: margen/fallback añadido a los timeouts cuando no hay transitionend.

// Configuración global (mutable) para duraciones y easing
const _config = {
  // Respeta preferencias de accesibilidad
  get reduce() {
    try {
      return (typeof window !== 'undefined' && window.matchMedia)
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    } catch (_) { return false; }
  },
  flipMs: 900,              // duración del flip
  blinkOneMs: 500,          // duración de un destello
  blinkCount: 3,            // número de parpadeos
  flipEasing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  fadeEasing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  tableroFadeMs: 2000,      // duración del fade-in en tablero y fade-out en d2
  loaderDwellMs: 1500,      // retardo antes de iniciar el loader
  loaderDurationMs: 2000,   // duración por defecto del loader (override por admin)
  tableroCountPhase: 'start', // 'start' (por defecto) o 'end'
  transitionSlackMs: 180,   // margen extra para fallbacks de transición
};

export function getAnimationConfig() {
  return { ..._config };
}

export function setAnimationConfig(partial) {
  if (!partial || typeof partial !== 'object') return;
  if (typeof partial.flipMs === 'number') _config.flipMs = Math.max(0, partial.flipMs);
  if (typeof partial.blinkOneMs === 'number') _config.blinkOneMs = Math.max(0, partial.blinkOneMs);
  if (typeof partial.blinkCount === 'number') _config.blinkCount = Math.max(0, partial.blinkCount);
  if (typeof partial.flipEasing === 'string') _config.flipEasing = partial.flipEasing;
  if (typeof partial.fadeEasing === 'string') _config.fadeEasing = partial.fadeEasing;
  if (typeof partial.tableroFadeMs === 'number') _config.tableroFadeMs = Math.max(0, partial.tableroFadeMs);
  if (typeof partial.loaderDwellMs === 'number') _config.loaderDwellMs = Math.max(0, partial.loaderDwellMs);
  if (typeof partial.loaderDurationMs === 'number') _config.loaderDurationMs = Math.max(0, partial.loaderDurationMs);
  if (typeof partial.tableroCountPhase === 'string') {
    const v = partial.tableroCountPhase === 'start' ? 'start' : 'end';
    _config.tableroCountPhase = v;
  }
  if (typeof partial.transitionSlackMs === 'number') _config.transitionSlackMs = Math.max(0, partial.transitionSlackMs);
}

// Aplica transformaciones de flip respetando CSS vars cuando están disponibles
export function applyFlipTransform(imgEl, flipped, reversed) {
  if (!imgEl) return;
  try {
    const supportsCSSVars = !!(typeof window !== 'undefined' && window.CSS && typeof window.CSS.supports === 'function' && (window.CSS.supports('(--a: 0)') || window.CSS.supports('color', 'var(--fake)')));
    const rotY = flipped ? 'rotateY(0deg)' : 'rotateY(180deg)';
    const rotZ = reversed ? 'rotate(180deg)' : 'rotate(0deg)';
    if (supportsCSSVars) {
      imgEl.style.setProperty('--tkna-rotY', rotY);
      imgEl.style.setProperty('--tkna-rotZ', rotZ);
    } else {
      imgEl.style.transform = rotY + ' ' + rotZ;
    }
  } catch (_) {}
}

// Realiza el flip con transición y resuelve cuando termina (o por timeout)
export async function flip(cardEl, imgEl, options = {}) {
  const { flipMs = _config.flipMs, flipEasing = _config.flipEasing } = options;
  const reduce = _config.reduce;
  try { if (cardEl) cardEl.classList.add('is-flipped'); } catch (_) {}
  if (imgEl) {
    try {
      imgEl.style.transitionProperty = 'transform';
      imgEl.style.transitionDuration = (reduce ? 0 : flipMs) + 'ms';
      imgEl.style.transitionTimingFunction = flipEasing;
    } catch (_) {}
    applyFlipTransform(imgEl, /*flipped*/ true, /*reversed*/ imgEl?.classList?.contains('flipSelector'));
  }
  if (!reduce && imgEl) {
    await new Promise((resolve) => {
      let done = false;
      const onEnd = (ev) => {
        if (done) return;
        if (ev && ev.target === imgEl && ev.propertyName === 'transform') {
          done = true;
          try { imgEl.removeEventListener('transitionend', onEnd); } catch (_) {}
          resolve();
        }
      };
      try { imgEl.addEventListener('transitionend', onEnd); } catch (_) {}
      setTimeout(() => { if (!done) { try { imgEl.removeEventListener('transitionend', onEnd); } catch (_) {} resolve(); } }, flipMs + 120);
    });
  }
}

// Inicia parpadeo (añade clase) y lo quita cuando termina. No bloquea.
export function startBlink(targetEl, options = {}) {
  const { blinkOneMs = _config.blinkOneMs, blinkCount = _config.blinkCount } = options;
  const reduce = _config.reduce;
  if (reduce || !targetEl) return { totalMs: 0, stop: () => {} };
  const totalMs = Math.max(0, blinkOneMs) * Math.max(0, blinkCount);
  try {
    // Aplicar tiempos centralizados a la animación CSS de la clase is-confirmed
    targetEl.style.animationDuration = `${Math.max(0, blinkOneMs)}ms`;
    targetEl.style.webkitAnimationDuration = `${Math.max(0, blinkOneMs)}ms`;
    targetEl.style.animationIterationCount = String(Math.max(1, blinkCount));
    targetEl.style.webkitAnimationIterationCount = String(Math.max(1, blinkCount));
    targetEl.classList.add('is-confirmed');
  } catch (_) {}
  const cleanup = () => {
    try {
      targetEl.classList.remove('is-confirmed');
      targetEl.style.animationDuration = '';
      targetEl.style.webkitAnimationDuration = '';
      targetEl.style.animationIterationCount = '';
      targetEl.style.webkitAnimationIterationCount = '';
    } catch (_) {}
  };
  const timer = setTimeout(cleanup, totalMs);
  return { totalMs, stop: () => { try { clearTimeout(timer); cleanup(); } catch (_) {} } };
}

// Secuencia típica: flip y luego parpadeo; devuelve tiempos usados
export async function flipAndBlink(cardEl, imgEl, options = {}) {
  const cfg = getAnimationConfig();
  const flipMs = options.flipMs ?? cfg.flipMs;
  const blinkOneMs = options.blinkOneMs ?? cfg.blinkOneMs;
  const blinkCount = options.blinkCount ?? cfg.blinkCount;

  await flip(cardEl, imgEl, { flipMs });
  const { totalMs: blinkMs } = startBlink(cardEl, { blinkOneMs, blinkCount });
  return { flipMs, blinkMs };
}
